import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom as rxjsFirstValueFrom} from 'rxjs';
import { UsuarioLogado } from '../interfaces/usuario/usuario-logado.interface';
import { jwtDecode } from 'jwt-decode';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Menu } from '../interfaces/menu.interface';
import { Permissao } from '../interfaces/acesso/usuario-permissao.interface';
import { ChamadosNaoVisualizados } from '../interfaces/chamado/chamado.interface';
import { PendenciasSgi } from '../interfaces/sgi-informacao-documentada/pendencias-sgi.interface';
import { PendenciasSacpm } from '../interfaces/sacpm/pendencias-sacpm.interface';

@Injectable({
  providedIn: 'root'
})
export class UsuarioService {

  private baseUrl = `${environment.APIEndpoint}usuarios`;
  private usuarioLogado = new BehaviorSubject<UsuarioLogado | undefined>(null);
  private chamadosNaoVisualizados = new BehaviorSubject<ChamadosNaoVisualizados | undefined>(null);
  private pendenciasSgi = new BehaviorSubject<PendenciasSgi>({ totalEnvio: 0, totalAprovacao: 0 });
  private pendenciasSacpm = new BehaviorSubject<PendenciasSacpm>({ totalMinhas: 0, totalValidacao: 0 });

  constructor(
    private http: HttpClient
  ) { }


  getUsuarioLogado() {
    return this.usuarioLogado;
  }

  // Indica se o usuário logado tem, nos seus menus, acesso à rota informada.
  // Mesma regra do authGuard (match exato ou caminho + id numérico), usada para
  // decidir o destino de retorno de telas acessadas via pendência (ex.: SACPM/SGI):
  // se não tem o módulo, volta para o dashboard em vez da listagem (acesso negado).
  temAcessoRota(url: string): boolean {
    const menus = this.usuarioLogado.value?.menus ?? this.obterMenusDoStorage();
    return this.hasMenuAccess(menus || [], url);
  }

  private obterMenusDoStorage(): any[] {
    try {
      const localUser = localStorage.getItem('usuario');
      return localUser ? (JSON.parse(localUser)?.menus ?? []) : [];
    } catch {
      return [];
    }
  }

  // Id do usuário logado, com o localStorage como fonte primária.
  //
  // O BehaviorSubject só é preenchido pelo authGuard, e sozinho ele não é confiável para
  // decidir permissão: em deep link ou num ponto do ciclo em que ainda não foi hidratado
  // ele devolve undefined, e quem compara "sou o responsável por este plano?" concluiria
  // que não é — desabilitando a tela para o dono legítimo dela.
  getUsuarioId(): number | null {
    const emMemoria = this.usuarioLogado.value?.id;
    if (typeof emMemoria === 'number') return emMemoria;

    try {
      const localUser = localStorage.getItem('usuario');
      if (!localUser) return null;
      const id = JSON.parse(localUser)?.id;
      return typeof id === 'number' ? id : null;
    } catch {
      return null;
    }
  }

  // Departamento do usuário logado. Vem do localStorage e não do BehaviorSubject porque
  // UsuarioLogado não carrega departamentoId — o objeto completo de GET /usuarios/{id},
  // que carrega, é o que fica gravado em localStorage['usuario'] no login.
  getDepartamentoId(): number | null {
    try {
      const localUser = localStorage.getItem('usuario');
      if (!localUser) return null;
      const id = JSON.parse(localUser)?.departamentoId;
      return typeof id === 'number' ? id : null;
    } catch {
      return null;
    }
  }

  // Departamento da qualidade, que valida a conclusão dos planos de ação do SACPM.
  // Serve só para mostrar ou esconder botão: quem barra de verdade é o WHERE do UPDATE
  // na API. Esconder o botão aqui não protege nada sozinho.
  isSgi(): boolean {
    return this.getDepartamentoId() === UsuarioService.DEPARTAMENTO_SGI;
  }

  private static readonly DEPARTAMENTO_SGI = 8;

  private hasMenuAccess(menus: any[], targetUrl: string): boolean {
    for (const menu of menus) {
      if (!menu?.caminho || menu.caminho === 'javascript:void(0)' || menu.caminho === '#') {
        if (menu?.subMenus?.length && this.hasMenuAccess(menu.subMenus, targetUrl)) {
          return true;
        }
        continue;
      }

      const menuPath = menu.caminho.replace(/\/+$/, '');
      const normalizedTargetUrl = targetUrl.replace(/\/+$/, '');

      if (menuPath === normalizedTargetUrl) {
        return true;
      }

      if (normalizedTargetUrl.startsWith(menuPath + '/')) {
        const remainingPath = normalizedTargetUrl.substring(menuPath.length + 1);
        if (/^[0-9]+$/.test(remainingPath)) {
          return true;
        }
      }

      if (menu.subMenus?.length && this.hasMenuAccess(menu.subMenus, normalizedTargetUrl)) {
        return true;
      }
    }
    return false;
  }

  getTotalChamados(){
    return this.chamadosNaoVisualizados;
  }

  // ===== Pendências de SGI (envio / aprovação) =====
  getPendenciasSgi(){
    return this.pendenciasSgi;
  }

  setPendenciasSgi(pendencias: PendenciasSgi){
    this.pendenciasSgi.next(pendencias);
  }

  setTotalSgiEnvio(total: number){
    const atual = this.pendenciasSgi.value ?? { totalEnvio: 0, totalAprovacao: 0 };
    this.pendenciasSgi.next({ ...atual, totalEnvio: total ?? 0 });
  }

  setTotalSgiAprovacao(total: number){
    const atual = this.pendenciasSgi.value ?? { totalEnvio: 0, totalAprovacao: 0 };
    this.pendenciasSgi.next({ ...atual, totalAprovacao: total ?? 0 });
  }

  // ===== Pendências de SACPM (minhas ações / validação) =====
  getPendenciasSacpm(){
    return this.pendenciasSacpm;
  }

  setTotalSacpmMinhas(total: number){
    const atual = this.pendenciasSacpm.value ?? { totalMinhas: 0, totalValidacao: 0 };
    this.pendenciasSacpm.next({ ...atual, totalMinhas: total ?? 0 });
  }

  setTotalSacpmValidacao(total: number){
    const atual = this.pendenciasSacpm.value ?? { totalMinhas: 0, totalValidacao: 0 };
    this.pendenciasSacpm.next({ ...atual, totalValidacao: total ?? 0 });
  }

  // Único ponto de escrita do contador, e sempre com o total que o servidor
  // calculou para o usuário logado — pela carga inicial do sidebar ou pelo
  // recarregamento que o evento de SignalR dispara.
  //
  // Não existe mais soma local de +1/-1 nem gravação no localStorage: era o
  // que fazia o número divergir da tela e nunca mais se corrigir, porque nada
  // o reconciliava com o banco.
  setTotalChamados(chamadosNaoVisualizados: ChamadosNaoVisualizados){
    return this.chamadosNaoVisualizados.next(chamadosNaoVisualizados);
  }

  async setUsuarioLogado(id: number, nome: string, email: string, permissoes: Permissao[], imagemPerfil: string, menus: Menu[], setoresAprovador: number[], chamadosNaoVisualizados: ChamadosNaoVisualizados) {
    var usuarioLogado: UsuarioLogado = { id: id, nome: nome, email: email, permissoes: permissoes, imagemPerfil: imagemPerfil, menus: menus, setoresAprovador: setoresAprovador, chamadosNaoVisualizados: chamadosNaoVisualizados };
    await this.usuarioLogado.next(usuarioLogado);
    await this.setTotalChamados(usuarioLogado.chamadosNaoVisualizados);
  }

  logout(){
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiraEm');
    localStorage.removeItem('usuario');

    Object.keys(sessionStorage)
      .filter(k => k.startsWith('sgi-pendentes-modal-'))
      .forEach(k => sessionStorage.removeItem(k));
    sessionStorage.removeItem('sacpmPendenciasVerificadas');
    sessionStorage.removeItem('sgi-fila-revisao-modal');
    sessionStorage.removeItem('justificativaAtrasoVerificada');
    sessionStorage.removeItem('motivoAgExpedicaoVerificado');
    sessionStorage.removeItem('pendenciasModalUltimoId');

    this.chamadosNaoVisualizados.next(null);
    this.pendenciasSgi.next({ totalEnvio: 0, totalAprovacao: 0 });
    this.pendenciasSacpm.next({ totalMinhas: 0, totalValidacao: 0 });

    this.removeUsuarioLogado();

  }

  removeUsuarioLogado(){
    this.usuarioLogado.next(undefined);
  }

  decodeToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (error) {
      console.error('Erro ao decodificar o token:', error);
      return null;
    }
  }

}
